<template>
    <div class="invite">
        <div v-if="invitedArtists.length" class="invited-artist-list">
            <p class="invited-artist-title">Already invited artists:</p>
            <InvitedArtistList
                :invited-artists="invited"
                @remove-artist="removedInvitedArtist"
            />
        </div>
        <div v-else class="invited-artist-list">
            <p class="invited-artist-title">You don't have any invited artists yet</p>
        </div>
        <div v-if="contacts.length" class="relevant-artists">
            <p class="title">Invite relevant artists from:</p>
            <div @click="selectAllContacts" class="contact-artist-pill">
                <p :class="contactSelected ? 'contact-artist-selected' : 'contact-artist'">
                    <img v-if="contactSelected" :src="getIcon('selected-icon.png')" />
                    My Saved Contacts {{ contacts.length }}
                    <img v-if="contactSelected" :src="getIcon('icons/purple-cancel.svg')" />
                </p>
            </div>
        </div>
        <div class="specific-artists">
            <div class="input-header">
                <p class="title">Invite specific artists:</p>
                <button @click="() => $emit('start-search')" class="search-artists-modal-btn">
                    Search Artists
                </button>
            </div>
            <div class="enter-artists">
                <input
                    v-model="$v.userData.invite.email.$model"
                    @keyup.enter="addArtists"
                    class="enter-artists-input"
                    placeholder="Add emails or pick from your contacts."
                />
                <button
                    @click="addArtists"
                    :disabled="!userData.invite.email"
                    class="add-artist-btn"
                >
                    Add
                </button>
                <div class="contacts-dropdown">
                    <ContactsDropdown
                        :show-contacts="showContacts"
                        :user-input="userData.invite.email"
                        :contacts="contacts"
                        @selected-contact="selectContact"
                    />
                </div>
            </div>
            <span
                v-if="$v.userData.invite.email.$dirty && !$v.userData.invite.email.email"
                class="error-msg"
            >
                Enter a valid email
            </span>
            <div class="specific-artists-content">
                <div v-if="userData.invite.artists.length" class="pills-container">
                    <div
                        v-for="(artist, index) in userData.invite.artists"
                        :key="index"
                        class="artists-pills"
                    >
                        <p v-if="artist.name">{{ artist.name }}</p>
                        <p v-else>{{ artist.email }}</p>
                        <p class="remove-email" @click="removeEmail(index)">x</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import { api } from '@/helpers/api'
    import InvitedArtistList from '@/components/booking-manager/booking-part/booked-artist/InvitedArtistList'
    import ContactsDropdown from '@/composables/features/venue-booking/useVenueContacts.js'
    import { useValidation } from '@/composables/utils/useValidation'

    const URLS = {
        xhr: {
            mutate: {
                relevantArtists: '/api/booking-manager/relevant-artists',
                artists: '/api/booking-manager/invite-artists',
                removeInvited: '/api/booking-manager/remove-invited-artist'
            }
        }
    }
    export default {
        name: 'Invite',
        components: {
            InvitedArtistList,
            ContactsDropdown
        },
        props: {
            userData: {
                type: Object,
                required: true
            },
            eventId: {
                type: Number,
                required: true
            },
            validation: {
                type: Object,
                required: false
            }
        },
        emits: ['start-search', 'update:validation'],
        validations: {
            userData: {
                invite: {
                    email: {
                        email(val) {
                            return /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*(,|;){1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4})[\W]*$/.test(
                                val
                            )
                        }
                    }
                }
            }
        },
        setup() {
            const { isValid } = useValidation()
            return {
                isValid
            }
        },
        data: () => ({
            load: false,
            artistInvited: false,
            contactSelected: false,
            contacts: [],
            invited: [],
            artists: []
        }),
        computed: {
            URLS: () => URLS,
            showContacts: (self) => {
                return !!self.userData?.invite.email.length
            },
            invitedArtists: (self) => {
                const formatted = []

                self.invited.forEach((artist) => {
                    if (artist.invited)
                        artist.invited.forEach((invite) => {
                            formatted.push({
                                id: artist.id,
                                artistId: artist.artistId,
                                invited: invite,
                                avatar: artist.avatar
                            })
                        })
                })

                return formatted.filter(
                    (value, index, invited) =>
                        invited.findIndex(
                            (v) => v.artistId === value.artistId && v.invited === value.invited
                        ) === index
                )
            }
        },
        watch: {
            contacts: {
                handler: function (contacts) {
                    if (
                        contacts.length &&
                        this.userData.invite.artists.length &&
                        contacts.every((contact) => {
                            return !!this.userData.invite.artists.find(
                                (artist) => artist.email === contact.band_email
                            )
                        })
                    ) {
                        this.contacts.forEach((contact) => {
                            this.$set(contact, 'selected', true)
                        })
                        this.contactSelected = true
                    }
                },
                immediate: true,
                deep: true
            }
        },
        methods: {
            getIcon(path) {
                return `/images/${path}`
            },
            async getRelevantArtists() {
                const data = await api({
                    method: 'post',
                    url: this.URLS.xhr.mutate.relevantArtists,
                    data: {
                        keywords: '',
                        is_need_all: true,
                        eventId: this.eventId
                    }
                })
                this.contacts = data.artists.contacts
                this.invited = data.artists.invitedArtists
            },
            selectContact(contact) {
                this.addContactToInvite(contact)
                this.userData.invite.email = ''
            },
            selectAllContacts() {
                if (this.contactSelected) {
                    this.contactSelected = false
                    this.contacts.forEach((contact) => this.removeContactFromInvite(contact))
                } else {
                    this.contactSelected = true
                    this.contacts.forEach((contact) => this.addContactToInvite(contact))
                }
            },
            addContactToInvite(contact) {
                this.$set(contact, 'selected', true)

                if (
                    !this.userData.invite.artists.find(
                        (artist) => artist.email === contact.band_email
                    )
                ) {
                    this.userData.invite.artists.push({
                        name: contact.band_name,
                        email: contact.band_email
                    })
                }
            },
            removeContactFromInvite(contact) {
                contact.selected = false
                const indexToDel = this.userData.invite.artists.indexOf(
                    this.userData.invite.artists.find(
                        (artist) => artist.email === contact.band_email
                    )
                )
                this.removeEmail(indexToDel)
            },
            addArtists() {
                if (this.isValid(this.$v)) {
                    const artistsArray = this.userData.invite.email.split(/[,;]/).map((a) => ({
                        email: a,
                        name: null
                    }))
                    const uniqueArtists = artistsArray.filter(
                        (e) =>
                            !this.userData.invite.artists.find(
                                (artist) => artist.email === e
                            ) &&
                            this.invited.every((a) =>
                                a.invited ? !a.invited.includes(e) : true
                            )
                    )
                    this.userData.invite.artists =
                        this.userData.invite.artists.concat(uniqueArtists)

                    this.userData.invite.email = ''
                }
            },
            removeEmail(index) {
                const contact = this.contacts.find(
                    (contact) =>
                        contact.band_email === this.userData.invite.artists[index]?.email
                )

                if (contact) {
                    contact.selected = false
                    this.contactSelected = false
                }

                this.userData.invite.artists.splice(index, 1)
            },
            async removedInvitedArtist(inviteId, invitedArtist, artistId) {
                if (artistId) {
                    const invite = this.invited.filter(
                        (a) => a.id === inviteId || a.artistId === artistId
                    )
                    invite.forEach((invite) => {
                        const indexToRemove = this.invited.indexOf(invite)

                        this.invited.splice(indexToRemove, 1)
                    })
                } else {
                    this.removeAdditionalRecipients(invitedArtist)
                }

                await this.removeInvitedArtist(inviteId, invitedArtist)
            },
            removeAdditionalRecipients(email) {
                const invitesIncludesEmail = this.invited.filter(
                    (artist) =>
                        artist.artistId === null &&
                        artist.invited &&
                        artist.invited.includes(email)
                )

                invitesIncludesEmail.forEach((invite) => {
                    const indexToRemove = invite.invited.indexOf(email)
                    invite.invited.splice(indexToRemove, 1)
                })
            },
            async removeInvitedArtist(inviteId, artist) {
                try {
                    await api({
                        method: 'post',
                        url: `${this.URLS.xhr.mutate.removeInvited}/${inviteId}`,
                        data: {
                            artist
                        }
                    })
                } catch (error) {
                    console.error(error)
                }
            }
        },
        async mounted() {
            await this.getRelevantArtists()
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    .invite {
        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }
        button:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
        .error-msg {
            color: $error;
            @include base-font-8;
            font-weight: 600;
            line-height: 130%;
            letter-spacing: 0.14px;
            text-wrap: nowrap;
        }
        .invited-artist-list {
            width: 100%;
            max-width: 1000px;
            display: flex;
            flex-direction: column;
            gap: 10px;

            .invited-artist-title {
                color: $darker-grey;
                @include base-font-32;
                line-height: 120%;
                letter-spacing: -0.64px;
            }
        }
        .relevant-artists {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            gap: 8px;

            .title {
                color: $white;
                @include base-font-20;
                font-weight: 600;
                line-height: 23px;
            }
            .contact-artist-pill {
                .contact-artist,
                .contact-artist-selected {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 0px;
                    padding: 6px 20px 8px 15px;
                    border-radius: 100px;
                    @include base-font-14;
                    font-weight: 700;
                    line-height: 130%;
                    letter-spacing: 0.14px;
                    cursor: pointer;

                    .view-link {
                        color: inherit;
                    }
                }

                .contact-artist {
                    border: 1px solid $lighter-grey;
                    color: $lighter-grey;
                }

                .contact-artist-selected {
                    border: 1px solid $violet;
                    color: $violet;
                }
            }
        }
        .specific-artists {
            width: 100%;
            max-width: 1000px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 8px;

            .input-header {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                width: 86%;

                .title {
                    color: $white;
                }

                .search-artists-modal-btn {
                    padding: 8px 12px;
                    margin: 0;
                    background: none;
                    border: none;
                    color: $darker-grey;
                    @include base-font-14;
                    line-height: 115%;
                    letter-spacing: 0.14px;
                }
            }
            .enter-artists {
                width: 100%;
                position: relative;
                display: flex;
                gap: 10px;
                margin-bottom: 8px;

                .enter-artists-input {
                    border-radius: 12px;
                    border: 1.5px solid $darker-grey;
                    background: $dark;
                    padding: 12px 15px 15px 15px;
                    width: 100%;
                    color: $white;
                    @include base-font-16;
                    font-weight: 700;
                    line-height: 125%;
                }
                .add-artist-btn {
                    padding: 11px 30px 12px 30px;
                    border-radius: 100px;
                    background: $lime;
                    border: 0;
                    color: $black;
                    @include base-font-18;
                    font-weight: 550;
                    line-height: 120%;
                }
                .contacts-dropdown {
                    position: absolute;
                    left: 55%;

                    @media screen and (max-width: 768px) {
                        left: 35%;
                    }
                }
            }
            .specific-artists-content {
                margin-bottom: 7px;
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
                flex-wrap: wrap;
                gap: 8px;

                .pills-container {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: center;
                    gap: 10px;
                    max-height: 90px;
                    overflow-y: scroll;
                    flex-wrap: wrap;

                    .artists-pills {
                        padding: 5px 15px;
                        border-radius: 100px;
                        border: 1px solid $lime;
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        justify-content: flex-start;
                        gap: 10px;

                        p {
                            color: $lime;
                            @include base-font-14;
                            font-weight: 700;
                            line-height: 130%;
                            letter-spacing: 0.14px;
                            margin: 0;
                        }
                    }

                    .invite-artist-image {
                        max-width: 28px;
                        max-height: 28px;
                        border-radius: 50%;
                    }

                    .remove-email {
                        cursor: pointer;
                    }
                }

                .pills-container::-webkit-scrollbar {
                    width: 0;
                }
            }
        }
    }
</style>
